import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppSyncSubscription, type JobStatusUpdate } from "./useAppSyncSubscription";
import {
  getJobStatus,
  releaseJobStatusPoller,
  tryAcquireJobStatusPoller,
  type JobStatusResponse,
} from "../utils/jobStatusApi";

const DEFAULT_POLL_INTERVAL_MS = 10_000;
const DEFAULT_REALTIME_GRACE_PERIOD_MS = 10_000;

type ConnectionState = "idle" | "connecting" | "connected" | "polling" | "failed";

export interface RealtimeJobCompletionPayload {
  jobId: string;
  update?: JobStatusUpdate;
  status?: JobStatusResponse | null;
}

export interface RealtimeJobFailurePayload {
  jobId: string;
  error: string;
  update?: JobStatusUpdate;
  status?: JobStatusResponse | null;
}

interface UseRealtimeJobOptions {
  jobId: string | null;
  userId: string | null;
  getAuthToken: () => Promise<string | null>;
  enabled?: boolean;
  pollIntervalMs?: number;
  realtimeGracePeriodMs?: number;
  initialStatusCheck?: boolean;
  onUpdate?: (update: JobStatusUpdate) => void;
  onStatus?: (status: JobStatusResponse) => void;
  onCompleted?: (payload: RealtimeJobCompletionPayload) => void | Promise<void>;
  onFailed?: (payload: RealtimeJobFailurePayload) => void | Promise<void>;
}

interface UseRealtimeJobResult {
  connectionState: ConnectionState;
  isPollingFallback: boolean;
}

let nextRealtimeJobOwnerId = 0;

function createRealtimeJobOwnerId(): string {
  nextRealtimeJobOwnerId += 1;
  return `realtime-job-${nextRealtimeJobOwnerId}`;
}

export function useRealtimeJob({
  jobId,
  userId,
  getAuthToken,
  enabled = true,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
  realtimeGracePeriodMs = DEFAULT_REALTIME_GRACE_PERIOD_MS,
  initialStatusCheck = false,
  onUpdate,
  onStatus,
  onCompleted,
  onFailed,
}: UseRealtimeJobOptions): UseRealtimeJobResult {
  const [isPollingFallback, setIsPollingFallback] = useState(false);
  const [connectionState, setConnectionState] = useState<ConnectionState>("idle");

  const ownerIdRef = useRef(createRealtimeJobOwnerId());
  const lastRealtimeAtRef = useRef(0);
  const sessionStartedAtRef = useRef(0);
  const activeJobIdRef = useRef<string | null>(null);
  const handledTerminalJobIdRef = useRef<string | null>(null);
  const pollingInFlightRef = useRef(false);

  const onUpdateRef = useRef(onUpdate);
  const onStatusRef = useRef(onStatus);
  const onCompletedRef = useRef(onCompleted);
  const onFailedRef = useRef(onFailed);

  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  useEffect(() => {
    onStatusRef.current = onStatus;
  }, [onStatus]);

  useEffect(() => {
    onCompletedRef.current = onCompleted;
  }, [onCompleted]);

  useEffect(() => {
    onFailedRef.current = onFailed;
  }, [onFailed]);

  useEffect(() => {
    activeJobIdRef.current = jobId;
  }, [jobId]);

  const handleCompleted = useCallback(async (payload: RealtimeJobCompletionPayload) => {
    if (handledTerminalJobIdRef.current === payload.jobId) {
      return;
    }

    handledTerminalJobIdRef.current = payload.jobId;
    setIsPollingFallback(false);
    await onCompletedRef.current?.(payload);
  }, []);

  const handleFailed = useCallback(async (payload: RealtimeJobFailurePayload) => {
    if (handledTerminalJobIdRef.current === payload.jobId) {
      return;
    }

    handledTerminalJobIdRef.current = payload.jobId;
    setIsPollingFallback(false);
    await onFailedRef.current?.(payload);
  }, []);

  const handleStatus = useCallback(
    async (status: JobStatusResponse | null) => {
      if (!status || !activeJobIdRef.current || status.jobId !== activeJobIdRef.current) {
        return;
      }

      onStatusRef.current?.(status);

      if (status.status === "COMPLETED" || status.isCompleted) {
        await handleCompleted({ jobId: status.jobId, status });
        return;
      }

      if (status.status === "FAILED" || status.isError) {
        await handleFailed({
          jobId: status.jobId,
          status,
          error: status.errorMessage || "Job failed.",
        });
      }
    },
    [handleCompleted, handleFailed],
  );

  const handleRealtimeUpdate = useCallback(
    async (update: JobStatusUpdate) => {
      if (!activeJobIdRef.current || update.jobId !== activeJobIdRef.current) {
        return;
      }

      lastRealtimeAtRef.current = Date.now();
      setIsPollingFallback(false);
      onUpdateRef.current?.(update);

      if (update.status === "COMPLETED") {
        await handleCompleted({ jobId: update.jobId, update });
        return;
      }

      if (update.status === "FAILED") {
        await handleFailed({
          jobId: update.jobId,
          update,
          error: update.errorMessage || "Job failed.",
        });
      }
    },
    [handleCompleted, handleFailed],
  );

  const subscription = useAppSyncSubscription({
    userId: enabled ? userId : null,
    jobId: enabled ? jobId : null,
    getAuthToken,
    onStatusUpdate: enabled ? handleRealtimeUpdate : undefined,
  });

  useEffect(() => {
    if (!enabled || !jobId) {
      activeJobIdRef.current = jobId;
      handledTerminalJobIdRef.current = null;
      lastRealtimeAtRef.current = 0;
      sessionStartedAtRef.current = 0;
      pollingInFlightRef.current = false;
      setIsPollingFallback(false);
      return;
    }

    activeJobIdRef.current = jobId;
    handledTerminalJobIdRef.current = null;
    lastRealtimeAtRef.current = Date.now();
    sessionStartedAtRef.current = Date.now();
    pollingInFlightRef.current = false;
    setIsPollingFallback(false);
  }, [enabled, jobId]);

  useEffect(() => {
    if (!enabled || !jobId || !initialStatusCheck) {
      return;
    }

    let cancelled = false;

    const checkStatus = async () => {
      const status = await getJobStatus(jobId);
      if (!cancelled) {
        await handleStatus(status);
      }
    };

    void checkStatus();

    return () => {
      cancelled = true;
    };
  }, [enabled, handleStatus, initialStatusCheck, jobId]);

  useEffect(() => {
    if (!enabled || !jobId || handledTerminalJobIdRef.current === jobId || isPollingFallback) {
      return;
    }

    const lastActivityAt = Math.max(sessionStartedAtRef.current, lastRealtimeAtRef.current);
    const elapsedMs = Math.max(Date.now() - lastActivityAt, 0);
    const delayMs = Math.max(realtimeGracePeriodMs - elapsedMs, 0);

    const timeoutId = window.setTimeout(() => {
      if (!activeJobIdRef.current || activeJobIdRef.current !== jobId || handledTerminalJobIdRef.current === jobId) {
        return;
      }

      setIsPollingFallback(true);
    }, delayMs);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [enabled, isPollingFallback, jobId, realtimeGracePeriodMs, subscription.isConnected]);

  useEffect(() => {
    if (!enabled || !jobId || !isPollingFallback) {
      return;
    }

    let cancelled = false;
    const ownerId = ownerIdRef.current;

    const poll = async () => {
      if (cancelled || pollingInFlightRef.current || handledTerminalJobIdRef.current === jobId) {
        return;
      }

      if (!tryAcquireJobStatusPoller(jobId, ownerId)) {
        return;
      }

      pollingInFlightRef.current = true;

      try {
        const status = await getJobStatus(jobId);
        if (!cancelled) {
          await handleStatus(status);
        }
      } finally {
        pollingInFlightRef.current = false;
        releaseJobStatusPoller(jobId, ownerId);
      }
    };

    void poll();

    const intervalId = window.setInterval(() => {
      void poll();
    }, pollIntervalMs);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      releaseJobStatusPoller(jobId, ownerId);
    };
  }, [enabled, handleStatus, isPollingFallback, jobId, pollIntervalMs]);

  useEffect(() => {
    if (!enabled || !jobId) {
      setConnectionState("idle");
      return;
    }

    if (isPollingFallback) {
      setConnectionState(subscription.isConnected ? "polling" : "failed");
      return;
    }

    if (subscription.isConnected) {
      setConnectionState("connected");
      return;
    }

    setConnectionState("connecting");
  }, [enabled, isPollingFallback, jobId, subscription.isConnected]);

  return useMemo(
    () => ({
      connectionState,
      isPollingFallback,
    }),
    [connectionState, isPollingFallback],
  );
}
